import { useQuery } from '@tanstack/react-query'
import MentorHeader from '../components/MentorHeader'
import React, { useEffect, useState } from 'react'
import { applyForMeeting, checkIsAlreadyApplied, getAllMentors, getMentorMeetingsForStartups } from '../utils/api'
import { FaEnvelope, FaLinkedin, FaPhone, FaSearch } from 'react-icons/fa'
import NavbarMainMentorDashboard from '../components/NavbarMainMentorDashboard'
import { Calendar, ChevronDown, ChevronRight, ChevronUp, Clock, Linkedin, UserPlus, Video, X } from 'lucide-react'
import { isStartupLoggedIn } from '../utils/isGuest'
import toast from 'react-hot-toast'
import PaymentModal from '../components/CashfreePaymentModal'
import { PaymentType } from '../utils/paymentTypes'
import { initializePayment } from '../utils/paymentUtils'
import { ClipLoader } from 'react-spinners'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from "framer-motion";

const MentorMainDashboard = () => {
    const [expandedMentors, setExpandedMentors] = useState({})
    const [search, setSearch] = useState("");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedMentor, setSelectedMentor] = useState(null);

    const toggleExpertiseExpansion = (mentorId) => {
        setExpandedMentors(prev => ({
            ...prev,
            [mentorId]: !prev[mentorId]
        }));
    };

    const {
        data: mentors,
        isLoading,
        refetch
    } = useQuery({
        queryKey: ["active-mentors"],
        queryFn: async () => {
            const data = await getAllMentors();
            return data.filter(mentor => mentor.isActive === 1);
        },

        staleTime: 1000 * 60 * 5,
        cacheTime: 1000 * 60 * 30,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
    });

    const { data: meetings, isLoading: isMeetingsLoading } = useQuery({
        queryKey: ["meetings", selectedMentor?._id],
        enabled: !!selectedMentor?._id,
        queryFn: () => getMentorMeetingsForStartups(selectedMentor._id)
    });

    const filteredMentors = mentors?.filter((mentor) =>
        mentor.name?.toLowerCase().includes(search.toLowerCase())
    );

    useEffect(() => {
        console.log(meetings)
    }, [meetings])

    return (
        <>
            <div>
                <NavbarMainMentorDashboard />
                <div className='container mx-auto px-4 sm:px-6 lg:px-8 py-6'>
                    <div className="space-y-3">
                        <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                            <FaSearch className="text-blue-500 text-sm" />
                            Search Mentors
                        </label>
                        <div className="relative">
                            <input
                                type="text"
                                name="name"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search by name..."
                                className="w-full px-4 py-3 pl-12 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all duration-300 hover:shadow-lg text-sm"
                            />
                            <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6 mt-5">


                        {isLoading ? (<MentorCardSkeleton />) : filteredMentors?.length === 0 ? (
                            <div className="col-span-full flex items-center justify-center py-14">
                                <p className="text-sm text-gray-500 text-center font-medium">
                                    No mentors found. Try adjusting your filters or check back later.
                                </p>
                            </div>) : (filteredMentors?.map((mentor, index) => {
                                const expertiseList = mentor.expertise
                                    ? mentor.expertise.split(',').map(skill => skill.trim())
                                    : ['General Mentoring'];

                                const isExpanded = expandedMentors[mentor._id];
                                const maxVisibleTags = 4; // Number of tags to show before collapsing
                                const hasMoreTags = expertiseList.length > maxVisibleTags;
                                const visibleTags = isExpanded
                                    ? expertiseList
                                    : expertiseList.slice(0, maxVisibleTags);

                                return (
                                    <motion.div
                                        initial={{ opacity: 0, y: 16 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: index * 0.06 }}
                                        whileHover={{ y: -4 }}
                                        className="h-full"
                                    >
                                        <div
                                            className="
      h-full
      bg-white
      rounded-2xl
      border border-gray-100
      p-6
      shadow-sm
      hover:shadow-md
      transition-all duration-300
      flex flex-col
    "
                                        >
                                            {/* ================= TOP ================= */}
                                            <div className="flex items-center gap-4">
                                                <img
                                                    src={mentor.image || "/placeholder.svg"}
                                                    alt={mentor.name}
                                                    className="w-14 h-14 rounded-full object-cover shrink-0"
                                                />

                                                <div className="min-w-0">
                                                    <h2 className="text-lg font-semibold text-gray-900 truncate">
                                                        {mentor.name}
                                                    </h2>
                                                    <p className="text-sm text-indigo-700 font-medium truncate">
                                                        {mentor.profession}
                                                    </p>
                                                    <p className="text-xs text-gray-500 truncate">
                                                        {mentor.email}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* ================= TAGS ================= */}
                                            <div className="mt-4">
                                                <div className="flex flex-wrap gap-2">
                                                    {visibleTags.slice(0, 4).map((skill, i) => (
                                                        <span
                                                            key={i}
                                                            className="px-3 py-1 text-xs rounded-full bg-indigo-50 text-indigo-700"
                                                        >
                                                            {skill}
                                                        </span>
                                                    ))}

                                                    {visibleTags.length > 4 && (
                                                        <span className="px-3 py-1 text-xs rounded-full bg-gray-100 text-gray-600">
                                                            +{visibleTags.length - 4}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            {/* ================= FOOTER (ALIGNED) ================= */}
                                            <div className="mt-auto pt-6 flex items-center justify-between">
                                                {/* LinkedIn placeholder keeps height consistent */}
                                                <div className="min-h-[20px]">
                                                    {mentor.linkedinProfile && (
                                                        <a
                                                            href={mentor.linkedinProfile}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            onClick={(e) => e.stopPropagation()}
                                                            className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                                                        >
                                                            <Linkedin className="w-4 h-4" />
                                                            LinkedIn
                                                        </a>
                                                    )}
                                                </div>

                                                <button
                                                    onClick={() => {
                                                        setSelectedMentor(mentor);
                                                        setIsModalOpen(true);
                                                    }}
                                                    className="
          relative overflow-hidden
          inline-flex items-center gap-2
          px-4 py-2
          bg-black text-white
          text-sm font-medium
          rounded-lg
          hover:bg-gray-900
          transition-all duration-300
          group
        "
                                                >
                                                    <span
                                                        className="
            absolute inset-0
            bg-[linear-gradient(120deg,transparent,rgba(255,255,255,0.4),transparent)]
            translate-x-[-100%]
            group-hover:translate-x-[100%]
            transition-transform duration-700
          "
                                                    />
                                                    <span className="relative z-10">View Appointments</span>
                                                    <ChevronRight className="relative z-10 w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    </motion.div>



                                );
                            }))}
                    </div>
                </div>
            </div>

            <MentorMeetingsModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                mentor={selectedMentor}
                isMeetingsLoading={isMeetingsLoading}
                meetings={meetings}
                setSelectedMentor={setSelectedMentor}

            />
        </>
    )
}

export default MentorMainDashboard

const MentorMeetingsModal = ({ isOpen, onClose, mentor, isMeetingsLoading, meetings, setSelectedMentor }) => {

    if (!isOpen || !mentor) return null;
    const backdropVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1 },
    };

    const modalVariants = {
        hidden: {
            opacity: 0,
            scale: 0.95,
            y: 20,
        },
        visible: {
            opacity: 1,
            scale: 1,
            y: 0,
            transition: {
                duration: 0.25,
                ease: "easeOut",
            },
        },
        exit: {
            opacity: 0,
            scale: 0.95,
            y: 20,
            transition: {
                duration: 0.2,
                ease: "easeIn",
            },
        },
    };


    return (
        <AnimatePresence>
            <motion.div
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
                variants={backdropVariants}
                initial="hidden"
                animate="visible"
                exit="hidden"
                onClick={onClose} // click outside to close
            >
                <motion.div
                    className="bg-white w-full max-w-3xl rounded-2xl shadow-xl p-6 relative max-h-[90vh] overflow-y-auto"
                    variants={modalVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    onClick={(e) => e.stopPropagation()} // prevent close on modal click
                >

                    {/* Header */}
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">
                                Meetings with {mentor.name}
                            </h2>
                            <p className="text-sm text-gray-500">
                                {mentor.profession}
                            </p>
                        </div>

                        <button
                            onClick={onClose}
                            className="p-2 rounded-full hover:bg-gray-100"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Meetings */}
                    {isMeetingsLoading ? (
                        <MeetingCardSkeleton />
                    ) : meetings?.data?.length === 0 ? (
                        <p className="text-center text-gray-500 py-10">
                            No meetings available for this mentor.
                        </p>
                    ) : (
                        <div className="space-y-4">
                            {meetings.data.map((meeting) => (
                                <MeetingCard
                                    key={meeting._id}
                                    meeting={meeting}
                                    setSelectedMentor={setSelectedMentor}
                                    onClose={onClose}
                                />
                            ))}
                        </div>
                    )}
                </motion.div>
            </motion.div>
        </AnimatePresence>

    );
};

const MeetingCard = ({ meeting, onClose, setSelectedMentor }) => {
    const [processing, setProcessing] = useState(false);
    const [paymentSessionId, setPaymentSessionId] = useState(null);
    const [currentOrderId, setCurrentOrderId] = useState(null);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const navigate = useNavigate()
    const onApplyNowClick = async (meeting) => {
        if (!isStartupLoggedIn()) {
            navigate('/login')
            toast.error("Please login to continue.");
            return;
        }

        setProcessing(meeting._id);
        const startupId = isStartupLoggedIn()

        try {

            const responceFromAlreadyExists = await checkIsAlreadyApplied({ meetingId: meeting._id, startupId })
            if (responceFromAlreadyExists.status == 400) {
                toast.error(responceFromAlreadyExists?.response?.data?.message)
                return
            }

            if (!meeting.isPaid) {
                const response = await applyForMeeting({ meetingId: meeting._id, startupId });
                toast.success(response.message);
                navigate("/startupmeetings")
                return;
            }


            const payload = {
                ...meeting,
                meetingId: meeting._id,
                startupId,
                isPaid: true
            };

            // 💳 Paid meeting
            sessionStorage.setItem(
                "mentor_meeting_booking",
                JSON.stringify(payload)
            );

            const token = localStorage.getItem("Startuptoken");
            let amount = Number(meeting.amount)

            const payment = await initializePayment({
                type: PaymentType.MENTOR_MEETING,
                amount,
                token,
                returnUrl: `${window.location.origin}/payment-callback`,
            });

            setPaymentSessionId(payment?.payment_session_id);
            setCurrentOrderId(payment?.order_id);
            setShowPaymentModal(true);


            toast.success(response.message);

        } catch (error) {

            const message =
                error?.response?.data?.message ||
                error?.message ||
                "Payment failed. Please try again.";

            toast.error(message);
            console.error(error);
        } finally {
            setProcessing(null);
            onClose()
            setSelectedMentor(null)
        }
    };

    const formatToAmPm = (time) => {
        if (!time) return "";

        const [hours, minutes] = time.split(":");
        const date = new Date();
        date.setHours(hours, minutes);

        return date.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
        });
    };

    return (
        <>
            <div
                key={meeting._id}
                className="
    group relative bg-white border border-gray-100 rounded-2xl
    shadow-sm hover:shadow-xl hover:shadow-indigo-500/10
    transition-all duration-300 p-6 overflow-hidden
  "
            >
                {/* Hover gradient overlay */}
                <div
                    className="
      absolute inset-0 bg-gradient-to-r
      from-indigo-500/5 to-purple-500/5
      opacity-0 group-hover:opacity-100
      transition-opacity rounded-2xl pointer-events-none
    "
                />

                {/* Main layout */}
                <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">

                    {/* LEFT CONTENT */}
                    <div className="flex items-start gap-4 min-w-0">
                        {/* Icon */}
                        <div className="p-3 rounded-2xl bg-indigo-50 text-indigo-600 shrink-0">
                            <Video className="w-6 h-6" />
                        </div>

                        {/* Text content */}
                        <div className="min-w-0">
                            {/* Title + Status */}
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                                <h3 className="font-bold text-lg text-gray-900 leading-tight break-words">
                                    {meeting.title}
                                </h3>

                                {meeting.status === "upcoming" ? (
                                    <span className="shrink-0 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-100">
                                        Upcoming
                                    </span>
                                ) : (
                                    <span className="shrink-0 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
                                        Completed
                                    </span>
                                )}
                            </div>

                            {/* Mentor */}
                            <p className="text-gray-500 text-sm font-medium">
                                with {meeting.mentorName}
                            </p>

                            {/* Meta info */}
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-3 text-sm text-gray-600">
                                <span className="flex items-center gap-1.5 font-medium">
                                    <Calendar className="w-4 h-4 text-indigo-500" />
                                    {new Date(meeting.date).toLocaleDateString("en-US", {
                                        month: "short",
                                        day: "numeric",
                                        year: "numeric",
                                    })}
                                </span>

                                <span className="flex items-center gap-1.5 font-medium">
                                    <Clock className="w-4 h-4 text-indigo-500" />
                                    {formatToAmPm(meeting.startTime)} - {formatToAmPm(meeting.endTime)}
                                </span>

                                <span
                                    className={`flex items-center gap-1.5 font-semibold ${meeting.isPaid ? "text-amber-600" : "text-indigo-600"
                                        }`}
                                >
                                    {meeting.isPaid ? `₹${meeting.amount.toLocaleString("en-IN")}` : "Free Session"}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* RIGHT ACTION */}
                    <div className="w-full sm:w-auto flex justify-start sm:justify-center">
                        <button
                            onClick={() => onApplyNowClick(meeting)}
                            // disabled={processing === meeting._id}
                            className="
          w-full sm:w-auto px-6 py-2
          text-sm font-medium text-white
          bg-gray-900 hover:bg-black
          rounded-xl shadow-sm
          transition-transform active:scale-95
          disabled:opacity-70
        "
                        >
                            {processing === meeting._id ? (
                                <ClipLoader color="#ffffff" size={20} />
                            ) : (
                                "Apply Now"
                            )}
                        </button>
                    </div>

                </div>
            </div>

            {showPaymentModal && (
                <PaymentModal
                    paymentSessionId={paymentSessionId}
                    orderId={currentOrderId}
                    onSuccess={() => {
                        toast.success("Payment successful!");
                        setShowPaymentModal(false);
                        setIsModalOpen(false);
                    }}
                    onFailure={(error) => {
                        toast.error(error.message || "Payment failed");
                        setProcessing(false);
                    }}
                    onClose={() => {
                        setShowPaymentModal(false);
                        setProcessing(false);
                    }}
                />
            )}
        </>
    );
};

const MeetingCardSkeleton = () => {
    return (
        <div className="animate-pulse bg-white border border-gray-100 rounded-2xl shadow-sm p-6">
            <div className="flex flex-col sm:flex-row sm:justify-between gap-4">

                {/* LEFT CONTENT */}
                <div className="flex-1 space-y-3">
                    {/* Title */}
                    <div className="h-5 w-3/4 bg-gray-200 rounded-md"></div>

                    {/* Mentor name */}
                    <div className="h-4 w-1/2 bg-gray-200 rounded-md"></div>

                    {/* Date & time */}
                    <div className="h-4 w-2/3 bg-gray-200 rounded-md"></div>

                    {/* Price */}
                    <div className="h-4 w-1/4 bg-gray-200 rounded-md"></div>
                </div>

                {/* RIGHT BUTTON */}
                <div className="w-full sm:w-auto">
                    <div className="h-10 w-full sm:w-28 bg-gray-300 rounded-xl"></div>
                </div>

            </div>
        </div>
    );
};



const MentorCardSkeleton = () => {
    return (
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 border border-white/60 animate-pulse">

            {/* Header */}
            <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 rounded-xl bg-slate-200" />
                <div className="flex-1 space-y-2">
                    <div className="h-4 bg-slate-200 rounded w-2/3" />
                    <div className="h-3 bg-slate-200 rounded w-1/2" />
                </div>
            </div>

            {/* Details */}
            <div className="space-y-2 mb-4">
                <div className="h-3 bg-slate-200 rounded w-full" />
                <div className="h-3 bg-slate-200 rounded w-3/4" />
                <div className="h-3 bg-slate-200 rounded w-2/3" />
            </div>

            {/* Expertise badge */}
            <div className="h-6 bg-slate-200 rounded-lg w-32" />
        </div>
    );
};
